import { z } from 'zod';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { newId } from '../../domain/id.ts';
import { ContactKindSchema, type Contact } from '../../domain/entities/contact.ts';

export const SaveContactInputSchema = z.object({
  kind: ContactKindSchema,
  name: z.string().trim().min(1, 'Give the contact a name').max(200),
  companyName: z.string().trim().max(200).optional(),
  email: z
    .union([z.string().trim().email('That email does not look right'), z.literal('')])
    .optional(),
  phone: z.string().trim().max(40).optional(),
  trade: z.string().trim().max(80).optional(),
  notes: z.string().trim().max(2000).optional(),
});

export type SaveContactInput = z.input<typeof SaveContactInputSchema>;

export interface SaveContactContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

export class ContactValidationError extends Error {
  readonly fieldErrors: Record<string, string>;

  constructor(fieldErrors: Record<string, string>) {
    const summary = Object.entries(fieldErrors)
      .map(([field, message]) => `${field}: ${message}`)
      .join('; ');
    super(`The contact details need attention — ${summary}`);
    this.name = 'ContactValidationError';
    this.fieldErrors = fieldErrors;
  }
}

/** Drop empty strings so an untouched optional field is absent, not blank. */
function optional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export async function createContact(
  context: SaveContactContext,
  input: SaveContactInput,
): Promise<Contact> {
  const parsed = SaveContactInputSchema.safeParse(input);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      fieldErrors[issue.path.join('.')] ??= issue.message;
    }
    throw new ContactValidationError(fieldErrors);
  }

  const now = context.clock.now().toISOString();
  const contact: Contact = {
    id: newId(),
    companyId: context.companyId,
    kind: parsed.data.kind,
    name: parsed.data.name,
    companyName: optional(parsed.data.companyName),
    email: optional(parsed.data.email),
    phone: optional(parsed.data.phone),
    trade: optional(parsed.data.trade),
    notes: optional(parsed.data.notes),
    status: 'active',
    certificates: [],
    defaultCostCodeIds: [],
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  await context.data
    .collection<Contact>({ companyId: context.companyId }, 'contacts')
    .create(contact);
  return contact;
}
